import { FormattedMessage, useIntl } from "react-intl";
import { Link } from "react-router";
import { Check, Loader2, Pencil, Sparkles, X } from "lucide-react";

import type { CampaignSlotCardData } from "@/components/campaign/campaign-slot-card";
import {
  resolveScheduledPostPreview,
  type ScheduledPostPreviewLogoAsset,
} from "@/lib/scheduled-post-preview";
import { cn } from "@/lib/utils";
import { isInstagramPublicationPending } from "@/lib/instagram-publication";
import { Button } from "@runcheetah/ui";

import { SocialPostPreviewCard } from "./social-post-preview-card";

export function GeneratedPostReviewCard({
  post,
  profileLabel,
  timeLabel,
  editPath,
  showScheduledLabel = true,
  logoAssets,
  onApprove,
  approvePending,
  onReject,
  rejectPending = false,
  onRetryGeneration,
  retryGenerationPending = false,
  onPublishNow,
  publishNowPending,
}: {
  post: CampaignSlotCardData;
  profileLabel: string;
  timeLabel: string;
  editPath: string | null;
  showScheduledLabel?: boolean;
  logoAssets?: ScheduledPostPreviewLogoAsset[];
  onApprove: () => void;
  approvePending: boolean;
  onReject?: () => void;
  rejectPending?: boolean;
  onRetryGeneration?: () => void;
  retryGenerationPending?: boolean;
  onPublishNow: () => void;
  publishNowPending: boolean;
}) {
  const intl = useIntl();
  const preview = resolveScheduledPostPreview({
    output: post.output,
    draft: post.draft ?? null,
    logoAssets,
  });
  const publicationStatus = post.publication?.status ?? null;
  const isFailed = post.status === "failed" || post.output.latestAttempt?.status === "failed";
  const isGenerated = post.status === "generated" && !isFailed;
  const isGenerating = post.status === "generating" && !isFailed;
  const isApproved = post.approvalStatus === "approved";
  const canApprove = isGenerated && !isApproved;
  const canReject = Boolean(onReject && isGenerated && post.approvalStatus !== "rejected");
  const canPublish =
    isGenerated &&
    isApproved &&
    post.instagramConnection?.isConnected === true &&
    !isInstagramPublicationPending(post.publication) &&
    publicationStatus !== "published";
  const approvalLabel = getGeneratedPostApprovalLabel({
    approvalStatus: post.approvalStatus,
    isGenerated,
    isFailed,
    intl,
  });
  const emptyVisualTitle = isFailed
    ? intl.formatMessage({
        id: "campaignDetail.failedTitle",
        defaultMessage: "Generation failed",
      })
    : post.status === "generating"
      ? intl.formatMessage({
          id: "campaignDetail.generatingTitle",
          defaultMessage: "Creative in progress",
        })
      : intl.formatMessage({
          id: "campaignDetail.queuedTitle",
          defaultMessage: "Creative queued",
        });
  const emptyVisualDescription = isFailed
    ? (post.output.latestAttempt?.lastError ??
      intl.formatMessage({
        id: "campaignDetail.failedDescription",
        defaultMessage: "No error details are available yet.",
      }))
    : post.status === "generating"
      ? intl.formatMessage({
          id: "campaignDetail.generatingDescription",
          defaultMessage:
            "The final render will appear here when the planned post finishes processing.",
        })
      : intl.formatMessage({
          id: "campaignDetail.queuedDescription",
          defaultMessage:
            "The final render will appear here once this planned post enters production.",
        });
  const editAction = editPath ? (
    <Button
      asChild
      variant="secondary"
      size="sm"
      className="h-8 rounded-lg px-3 text-[12px] font-extrabold text-foreground"
    >
      <Link to={editPath}>
        <Pencil className="h-4 w-4" aria-hidden="true" />
        <FormattedMessage id="campaignDetail.edit" defaultMessage="Edit" />
      </Link>
    </Button>
  ) : (
    <Button
      variant="secondary"
      size="sm"
      className="h-8 rounded-lg px-3 text-[12px] font-extrabold text-foreground"
      disabled
    >
      <Pencil className="h-4 w-4" aria-hidden="true" />
      <FormattedMessage id="campaignDetail.edit" defaultMessage="Edit" />
    </Button>
  );
  const approveAction = (
    <Button
      type="button"
      size="sm"
      variant="brand"
      className="h-8 rounded-lg px-4 text-[12px] font-extrabold"
      onClick={onApprove}
      disabled={!canApprove || approvePending || rejectPending}
    >
      {approvePending ? (
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
      ) : (
        <Check className="h-4 w-4" aria-hidden="true" />
      )}
      <FormattedMessage id="campaignDetail.approve" defaultMessage="Approve" />
    </Button>
  );
  const rejectAction = onReject ? (
    <Button
      type="button"
      variant="outline"
      size="sm"
      className="rounded-xl text-destructive hover:text-destructive"
      onClick={onReject}
      disabled={!canReject || rejectPending || approvePending}
    >
      {rejectPending ? (
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
      ) : (
        <X className="h-4 w-4" aria-hidden="true" />
      )}
      <FormattedMessage id="campaignDetail.deny" defaultMessage="Deny" />
    </Button>
  ) : null;
  const approvedAction = (
    <div className="inline-flex h-8 items-center rounded-lg bg-background/92 px-4 text-[12px] font-extrabold text-foreground shadow-sm backdrop-blur-sm">
      <FormattedMessage
        id="campaignDetail.generatedContentApproval.approved"
        defaultMessage="Approved"
      />
    </div>
  );
  const mediaOverlay = isGenerated ? (
    <div className="pointer-events-none absolute inset-0 z-10 hidden items-center justify-center bg-black/0 opacity-0 transition-[background-color,opacity] duration-200 group-hover:bg-black/16 group-hover:opacity-100 group-focus-within:bg-black/16 group-focus-within:opacity-100 sm:flex">
      <div className="pointer-events-auto flex items-center gap-2">
        {editAction}
        {isApproved ? approvedAction : approveAction}
      </div>
    </div>
  ) : undefined;

  return (
    <article
      className={cn(
        "relative overflow-hidden rounded-[1.4rem] border border-border bg-card shadow-soft",
        isGenerated && !isApproved && "border-brand/60 bg-brand/5 ring-2 ring-brand/20",
        isGenerating &&
          "border-brand/45 bg-[linear-gradient(180deg,var(--card),rgba(200,255,0,0.08))] ring-2 ring-brand/15",
        isFailed && "border-destructive/35 bg-destructive/5",
      )}
    >
      {isGenerating ? (
        <span
          className="pointer-events-none absolute inset-x-[-20%] top-0 z-10 h-px bg-[linear-gradient(90deg,transparent,rgba(200,255,0,0.9),transparent)] bg-[length:220%_100%] motion-safe:animate-[shimmer_2.4s_ease-in-out_infinite]"
          aria-hidden="true"
        />
      ) : null}
      <SocialPostPreviewCard
        preview={preview}
        timeLabel={timeLabel}
        profileLabel={profileLabel}
        approvalLabel={approvalLabel}
        showApprovalBadge={true}
        muted={post.approvalStatus === "rejected"}
        className="rounded-none border-0 shadow-none"
        captionFallback={intl.formatMessage({
          id: "campaignDetail.previewCaptionPending",
          defaultMessage: "Caption will appear here once generated.",
        })}
        emptyVisualTitle={emptyVisualTitle}
        emptyVisualDescription={emptyVisualDescription}
        emptyVisual={isGenerating ? <GeneratedPostVisualSpinner /> : undefined}
        mediaOverlay={mediaOverlay}
      />

      <div className="space-y-3 p-4">
        {showScheduledLabel ? (
          <p className="text-xs text-muted-foreground">
            <FormattedMessage
              id="campaignDetail.generatedPostCreated"
              defaultMessage="Scheduled {date}"
              values={{ date: timeLabel }}
            />
          </p>
        ) : null}

        <div className="grid gap-2 sm:hidden sm:grid-cols-3">
          {editAction}
          {rejectAction}
          {isApproved ? null : approveAction}
        </div>

        <div className="flex items-center gap-2">
          {rejectAction ? <div className="hidden sm:block">{rejectAction}</div> : null}

          {isFailed && onRetryGeneration ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="rounded-xl"
              onClick={onRetryGeneration}
              disabled={retryGenerationPending}
            >
              {retryGenerationPending ? (
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              ) : (
                <Sparkles className="h-4 w-4" aria-hidden="true" />
              )}
              <FormattedMessage
                id="campaignDetail.retryGeneration"
                defaultMessage="Retry generation"
              />
            </Button>
          ) : null}

          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-8 rounded-xl px-2 text-xs text-muted-foreground sm:ml-auto"
            onClick={onPublishNow}
            disabled={!canPublish || publishNowPending}
          >
            {publishNowPending ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
            ) : (
              <Sparkles className="h-3.5 w-3.5" aria-hidden="true" />
            )}
            {intl.formatMessage({
              id: "campaignDetail.publishNow",
              defaultMessage: "Publish now",
            })}
          </Button>
        </div>

        {post.publication?.lastError ? (
          <p className="text-sm leading-6 text-destructive">{post.publication.lastError}</p>
        ) : null}
      </div>
    </article>
  );
}

export function GeneratedPostVisualSpinner() {
  return (
    <div className="relative flex aspect-4/5 items-center justify-center overflow-hidden bg-[#f6f3ee] p-8">
      <div
        className="absolute inset-0 bg-[radial-gradient(circle_at_50%_36%,rgba(200,255,0,0.34),transparent_32%),linear-gradient(115deg,transparent_0%,rgba(255,255,255,0.62)_42%,transparent_58%)] bg-[length:100%_100%,220%_220%] motion-safe:animate-[shimmer_3s_ease-in-out_infinite]"
        aria-hidden="true"
      />
      <div className="relative z-10 rounded-[1.6rem] border border-white/75 bg-white/62 px-6 py-5 text-center shadow-[0_18px_48px_rgba(31,31,34,0.12)] backdrop-blur-md">
        <div className="relative mx-auto h-12 w-12">
          <span
            className="absolute inset-0 rounded-full bg-brand/35 motion-safe:animate-ping"
            aria-hidden="true"
          />
          <span
            className="absolute inset-1 rounded-full border border-brand/70 bg-[#fffefa] shadow-[0_0_26px_rgba(200,255,0,0.4)]"
            aria-hidden="true"
          />
          <Loader2
            className="absolute inset-0 m-auto h-6 w-6 animate-spin text-[#1f1f22]"
            aria-hidden="true"
          />
        </div>
        <p className="mt-4 text-[13px] font-semibold leading-5 text-[#1f1f22]">
          <FormattedMessage
            id="campaignDetail.generatingVisualLabel"
            defaultMessage="Generating…"
          />
        </p>
        <span className="mt-3 flex justify-center gap-1.5" aria-hidden="true">
          {["delay-0", "delay-150", "delay-300"].map((delayClass) => (
            <span
              key={delayClass}
              className={cn(
                "h-1.5 w-1.5 rounded-full bg-brand shadow-[0_0_12px_rgba(200,255,0,0.6)] motion-safe:animate-pulse",
                delayClass,
              )}
            />
          ))}
        </span>
      </div>
    </div>
  );
}

function getGeneratedPostApprovalLabel({
  approvalStatus,
  isGenerated,
  isFailed,
  intl,
}: {
  approvalStatus: CampaignSlotCardData["approvalStatus"];
  isGenerated: boolean;
  isFailed: boolean;
  intl: ReturnType<typeof useIntl>;
}) {
  if (approvalStatus === "approved") {
    return intl.formatMessage({
      id: "campaignDetail.generatedContentApproval.approved",
      defaultMessage: "Approved",
    });
  }

  if (approvalStatus === "rejected") {
    return intl.formatMessage({
      id: "campaignDetail.generatedContentApproval.denied",
      defaultMessage: "Denied",
    });
  }

  if (isGenerated) {
    return intl.formatMessage({
      id: "campaignDetail.generatedContentApproval.pending",
      defaultMessage: "Pending review",
    });
  }

  if (isFailed) {
    return intl.formatMessage({
      id: "campaignDetail.plannedStatus.failed",
      defaultMessage: "Failed",
    });
  }

  return intl.formatMessage({
    id: "campaignDetail.plannedStatus.generating",
    defaultMessage: "Generating",
  });
}
